import React, { useState, useRef } from 'react';
import { Search, Printer, Barcode as BarcodeIcon, ChevronDown, ChevronUp, RefreshCw, Save, Edit3, Wand2, X } from 'lucide-react';
import { Product, UserRole, ToastMessage, Collections } from '../../types';
import ConfirmationModal from '../UI/ConfirmationModal';
import { printLabel } from '@/utils';

interface BarcodeDirectoryProps {
  products: Product[];
  onUpdateProduct: (product: Product) => void | Promise<void>;
  collections: Collections[];
  userRole: UserRole | null;
  addToast: (type: ToastMessage['type'], title: string, message: string) => void;
}

const BarcodeDirectory: React.FC<BarcodeDirectoryProps> = ({
  products,
  onUpdateProduct,
  userRole,
  addToast
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(new Set());
  const [editingBarcode, setEditingBarcode] = useState<{ productId: string, size: string, value: string } | null>(null);
  const [pendingBulkGenerate, setPendingBulkGenerate] = useState<Product | null>(null);
  const [printJob, setPrintJob] = useState<{ title: string; barcode: string; price: number; size: string } | null>(null);
  const [printQty, setPrintQty] = useState('1');
  const printQtyRef = useRef<HTMLInputElement>(null);

  const isReadOnly = userRole === 'Viewer';

  const filteredProducts = products.filter(p => {
    const s = searchTerm.toLowerCase();
    const nameMatch = p.name?.toLowerCase().includes(s);
    const idMatch = p.id?.toLowerCase().includes(s);
    const sizeMatch = (p.stocks || []).some(sz => {
      const variantBarcode = sz.barcode || `${p.id}-${sz.size}`;
      return variantBarcode.toLowerCase().includes(s);
    });
    return nameMatch || idMatch || sizeMatch;
  });

  const toggleExpand = (id: string) => {
    const next = new Set(expandedProductIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedProductIds(next);
  };

  const generateUniqueBarcode = (name: string, size: string): string => {
    // Split name into words, strip punctuation
    const words = name.replace(/[^a-zA-Z0-9 ]/g, ' ').trim().split(/\s+/).filter(Boolean);
    let first: string;
    let second: string;
    if (words.length === 1) {
      // Single word: split at character midpoint
      const mid = Math.ceil(words[0].length / 2);
      first = words[0].slice(0, mid);
      second = words[0].slice(mid);
    } else {
      // Multiple words: first half of words vs second half
      const mid = Math.ceil(words.length / 2);
      first = words.slice(0, mid).join('');
      second = words.slice(mid).join('');
    }
    const a = first.slice(0, 3).toUpperCase();
    const b = (second.slice(0, 3) || first.slice(-2)).toUpperCase();
    return `${a}-${b}-${size}`;
  };

  const handleSaveBarcode = (product: Product, size: string, newValue: string) => {
    const updatedStocks = product.stocks.map(s =>
      s.size === size ? { ...s, barcode: newValue } : s
    );
    onUpdateProduct({ ...product, stocks: updatedStocks });
    setEditingBarcode(null);
    addToast('success', 'Identity Updated', `Barcode for ${product.name} (Size ${size}) saved`);
  };

  const finalizeBulkGenerate = () => {
    if (!pendingBulkGenerate || isReadOnly) return;
    const product = pendingBulkGenerate;
    const updatedStocks = product.stocks.map(s => ({
      ...s,
      barcode: generateUniqueBarcode(product.name, s.size)
    }));
    onUpdateProduct({ ...product, stocks: updatedStocks });
    setPendingBulkGenerate(null);
    addToast('success', 'Bulk Generation Complete', `Refreshed ${updatedStocks.length} variants for ${product.name}`);
  };

  const handlePrintAll = () => {
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      addToast('error', 'Print Blocked', 'Please enable popups to generate barcode PDF');
      return;
    }

    const cards = filteredProducts.flatMap(p => {
      return (p.stocks || []).map(s => {
        const variantBarcode = s.barcode || generateUniqueBarcode(p.name, s.size);
        const barcodeUrl = `https://barcodeapi.org/api/128/${encodeURIComponent(variantBarcode)}`;
        return `
          <div class="sticker">
            <div class="product-name">${p.name}</div>
            <div class="variant">SIZE: ${s.size}</div>
            <img src="${barcodeUrl}" class="barcode-img" />
            <div class="price">Rs. ${p.price}</div>
          </div>
        `;
      });
    }).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Bulk Barcode Print</title>
          <style>
            @page { margin: 0; size: 50mm 25mm; }
            body { margin: 0; padding: 0; font-family: 'Inter', sans-serif; background: #fff; }
            .sticker { 
              width: 48mm; 
              height: 23mm; 
              padding: 1mm;
              display: flex; 
              flex-direction: column; 
              align-items: center; 
              justify-content: center;
              border: 0.1mm solid #eee;
              page-break-after: always;
              overflow: hidden;
              text-align: center;
            }
            .product-name { font-size: 7pt; font-weight: 700; text-transform: uppercase; white-space: nowrap; overflow: hidden; width: 100%; }
            .variant { font-size: 5pt; font-weight: 600; color: #666; margin-bottom: 1mm; }
            .barcode-img { height: 8mm; width: 40mm; object-fit: contain; }
            .price { font-size: 8pt; font-weight: 900; margin-top: 0.5mm; }
          </style>
        </head>
        <body onload="var imgs=document.images,n=imgs.length,done=0;if(n===0){setTimeout(()=>{window.print();window.close();},400);}else{for(var i=0;i<n;i++){imgs[i].onload=imgs[i].onerror=function(){if(++done===n){setTimeout(()=>{window.print();window.close();},400);}};if(imgs[i].complete)imgs[i].onload();}}">
          ${cards || '<p style="text-align:center; padding: 20px;">No barcodes to print.</p>'}
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleTSCPrint = (title: string, barcode: string, price: number, size: string) => {
    setPrintQty('1');
    setPrintJob({ title, barcode, price, size });
    // Focus the qty input after the modal renders
    setTimeout(() => printQtyRef.current?.focus(), 80);
  };

  const confirmPrint = async () => {
    if (!printJob) return;
    const qty = parseInt(printQty);
    if (isNaN(qty) || qty <= 0) {
      addToast('error', 'Invalid Quantity', 'Please enter a number greater than 0');
      return;
    }
    setPrintJob(null);
    try {
      for (let i = 0; i < qty; i++) {
        await printLabel('TSC TTP-244 Pro', printJob.title, printJob.barcode, printJob.price, printJob.size);
      }
      addToast('success', 'Print Job Sent', `${qty} label(s) sent to TSC printer`);
    } catch (error) {
      console.error(error);
      addToast('error', 'Print Error', 'Check QZ Tray connection');
    }
  };

  return (
    <div className="p-8 h-full overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Identity & Barcoding</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">
              {isReadOnly ? 'Audit Point-of-Sale Identities' : 'Generate & Manage Unique Product Serials'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
              <input
                type="text"
                placeholder="Search SKU or Serial..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-bold placeholder-gray-300 outline-none focus:ring-4 focus:ring-indigo-500/10 w-48 md:w-64 transition-all"
              />
            </div>
            <button
              onClick={handlePrintAll}
              className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-xl shadow-indigo-100 dark:shadow-none"
            >
              <Printer size={16} />
              Print All (PDF)
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4">
          {filteredProducts.map((product) => {
            const isExpanded = expandedProductIds.has(product.id);
            const variants = product.stocks || [];

            return (
              <div
                key={product.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-all"
              >
                <div
                  className="p-6 flex items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  onClick={() => toggleExpand(product.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-xl text-indigo-600 dark:text-indigo-400 shrink-0">
                      <BarcodeIcon size={24} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-black text-gray-900 dark:text-white uppercase truncate">{product.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">{product.collection}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {!isReadOnly && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPendingBulkGenerate(product); }}
                        className="p-2.5 bg-gray-50 dark:bg-gray-700 text-gray-500 hover:text-indigo-600 rounded-xl transition-all flex items-center gap-2 group/btn"
                        title="Auto-Generate Barcodes for all Variants"
                      >
                        <Wand2 size={16} />
                        <span className="text-[9px] font-black uppercase tracking-widest hidden lg:block">Auto-Fill All</span>
                      </button>
                    )}
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{variants.length} Variants</p>
                    </div>
                    <button className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-500">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-6 border-t border-gray-100 dark:border-gray-700 animate-in slide-in-from-top-2 duration-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {variants.map((size, sIdx) => {
                        const variantBarcode = size.barcode || generateUniqueBarcode(product.name, size.size);
                        const barcodeUrl = `https://barcodeapi.org/api/128/${encodeURIComponent(variantBarcode)}`;
                        const isEditing = editingBarcode?.productId === product.id && editingBarcode?.size === size.size;
                        return (
                          <div key={sIdx} className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center gap-3 group/item shadow-sm hover:shadow-md transition-all">
                            <div className="flex justify-between w-full items-center">
                              <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase">Size {size.size}</span>
                              <span className="text-[10px] font-black text-gray-400">Rs. {product.price}</span>
                            </div>

                            <div className="bg-white p-3 rounded-lg border border-gray-50 flex items-center justify-center min-h-[70px] w-full">
                              <img
                                src={barcodeUrl}
                                alt="Variant Barcode"
                                className="h-10 w-full object-contain mix-blend-multiply"
                              />
                            </div>

                            <div className="w-full space-y-2">
                              <div className="flex items-center gap-2">
                                {isEditing ? (
                                  <div className="flex-1 flex gap-1">
                                    <input
                                      autoFocus
                                      value={editingBarcode.value}
                                      onChange={(e) => setEditingBarcode({ ...editingBarcode, value: e.target.value.toUpperCase() })}
                                      className="flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-700 border border-indigo-200 rounded text-[10px] font-mono font-bold uppercase outline-none"
                                    />
                                    <button
                                      onClick={() => handleSaveBarcode(product, size.size, editingBarcode.value)}
                                      className="p-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                    >
                                      <Save size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <p className="flex-1 text-[10px] font-mono text-gray-500 font-bold truncate">{variantBarcode}</p>
                                    {!isReadOnly && (
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => setEditingBarcode({ productId: product.id, size: size.size, value: variantBarcode })}
                                          className="p-1 text-gray-300 hover:text-indigo-600 transition-colors"
                                        >
                                          <Edit3 size={14} />
                                        </button>
                                        <button
                                          onClick={() => handleSaveBarcode(product, size.size, generateUniqueBarcode(product.name, size.size))}
                                          className="p-1 text-gray-300 hover:text-emerald-600 transition-colors"
                                          title="Re-generate Unique Barcode"
                                        >
                                          <RefreshCw size={14} />
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTSCPrint(product.name, variantBarcode, product.price, size.size);
                                }}
                                className="w-full py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2"
                              >
                                <Printer size={12} /> Print Label
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!pendingBulkGenerate}
        onClose={() => setPendingBulkGenerate(null)}
        onConfirm={finalizeBulkGenerate}
        title="Refresh Article Serials?"
        message={`This will overwrite all existing barcodes for "${pendingBulkGenerate?.name}" with newly generated unique IDs. This action is logged for audit.`}
        confirmText="Generate All"
        type="warning"
      />

      {/* Print Label Quantity Modal — replaces window.prompt() */}
      {printJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Print Labels</h3>
                <p className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase tracking-widest">{printJob.title} · Size {printJob.size}</p>
              </div>
              <button onClick={() => setPrintJob(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Quantity</label>
                <input
                  ref={printQtyRef}
                  type="number"
                  min="1"
                  max="100"
                  value={printQty}
                  onChange={e => setPrintQty(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') confirmPrint(); if (e.key === 'Escape') setPrintJob(null); }}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-black text-lg text-center focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none"
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setPrintJob(null)} className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
                <button onClick={confirmPrint} className="flex-[2] py-2.5 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-indigo-100 dark:shadow-none">
                  <Printer size={14} /> Send to Printer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodeDirectory;